import React, { useState } from 'react'
import { FormLabel, Grid, Stack, Typography } from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'

import { GRAY_900 } from 'constants/styling/theme'
import type { ProductKind } from 'constants/product'
import type { MeetingDTO, MeetingMode, ResolvedInstructionOption } from 'models/purchaseFlowRevamp'
import { InputWrap } from 'components/common/InputWrap'
import { MUICheckbox } from 'components/common/MUICheckBox'
import { MUICheckCard } from 'components/common/MUICards'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'

import { PfRevampEventName, usePfRevampAnalytics } from '../../../analytics'
import { PfInlineLoading } from '../../../components/PfInlineLoading.component'
import { formatInstructionOptionPrice } from '../extras.helpers'
import { MEETING_MODE_KIND } from '../meeting.helpers'
import {
  choiceMode,
  choicePrefills,
  expandMeetingChoices,
  MEETING_CHOICE_KEY,
  selectedMeetingChoice,
  type MeetingChoice
} from '../meetingChoice.helpers'
import { KeyPickupAddressField } from './meeting/KeyPickupAddressField.component'
import { type MeetingContact, MeetingContactFields, useMeetingFieldUi } from './meeting/MeetingContactFields.component'

interface Props {
  value: MeetingDTO | undefined
  onChange: (next: MeetingDTO | undefined) => void
  /** The meeting modes the instruction catalogue offers. */
  availableModes: MeetingMode[]
  /** Shows a loading row instead of the cards, so no possibly-invalid subset flashes. */
  isModesLoading: boolean
  /** The organization options by kind; a mode whose option has a positive amount shows its price. */
  organizationInstructions: Partial<Record<ProductKind, ResolvedInstructionOption>>
  currency?: string
  /** Restricts the key-pickup address search and the phone prefix. */
  countryCode?: string
  /** Where the key-pickup map opens (the property). */
  mapCenter?: { lat: number; lng: number }
  /** The order user's contact details, offered by "me" cards and the key-pickup checkbox. */
  contactPrefill?: { name?: string; phone?: string; email?: string }
  /** Whose details the prefill copy speaks of: the logged-in user, or an admin's client. */
  prefillTarget?: 'ME' | 'OTHER_CLIENT'
  /** Reports the phone input's verdict on the visible number, for the step's gate. */
  onPhoneValidityChange: (invalid: boolean) => void
  /** A measurement-only cart: a technician attends, so legacy's `_MEASUREMENT` copy applies. */
  isMeasurementOnly?: boolean
}

/** A contact patch in the `contact*` spelling the on-site and key-pickup branches use. */
const toContactFields = (patch: MeetingContact) => ({
  ...('name' in patch ? { contactName: patch.name } : {}),
  ...('phone' in patch ? { contactPhone: patch.phone } : {}),
  ...('email' in patch ? { contactEmail: patch.email } : {}),
})

/**
 * How the creative gets on site: one card per meeting choice, the selected one opening its contact
 * form in legacy's field order. Shown only when a product needs a creative on site, so a meeting is
 * always required; the date is asked by the shooting-schedule section above.
 *
 * Any mode whose organization option has a price shows it; a key pickup also notes its travel fee,
 * which the backend charges by distance.
 */
export const MeetingSection: React.FC<Props> = ({
  value,
  onChange,
  availableModes,
  isModesLoading,
  organizationInstructions,
  currency,
  countryCode,
  mapCenter,
  contactPrefill,
  prefillTarget = 'ME',
  onPhoneValidityChange,
  isMeasurementOnly = false,
}) => {
  const { t, i18n } = useTranslation(['purchase_flow_revamp', 'purchase_flow', 'order', 'instruction_option_field', 'product_kind_description'])
  const trackAnalytics = usePfRevampAnalytics()
  const fieldUi = useMeetingFieldUi()

  // Read at render, never seeded: a sole offered mode is the answer, and a stored mode no longer
  // offered (resumed draft) is ignored. The field writers stamp `mode` on the first keystroke.
  const storedMode = value?.mode
  const mode = storedMode && availableModes.includes(storedMode)
    ? storedMode
    : availableModes.length === 1 ? availableModes[0] : undefined

  // Every field write builds on this, never on `value`: a stale mode's sub-branch would otherwise ship
  // two organization methods at once.
  // A card can be lit without a click (a sole mode, a draft with no details yet), so the first write records
  // which one the client is looking at; the Summary names the card from it.
  const base = (): MeetingDTO => {
    const next: MeetingDTO = storedMode === mode
      ? { ...value, mode: mode as MeetingMode }
      : { mode: mode as MeetingMode }
    if (next.contactIsSelf === undefined && selectedChoice && choiceMode(selectedChoice) === mode && (mode === 'ON_SITE' || mode === 'THIRD_PARTY')) {
      next.contactIsSelf = choicePrefills(selectedChoice)
    }
    return next
  }

  /** "I am responsible for the keys": kept on the meeting like the on-site pick, never serialized into the order. */
  const keyPickupFilledIn = mode === 'KEY_PICKUP' && value?.contactIsSelf === true

  const setMode = (next: MeetingMode) => {
    // Seed defaults on mode change; preserve fields already entered for the NEW
    // mode, drop unrelated sub-branches.
    const base: MeetingDTO = { mode: next }
    if (next === 'ON_SITE') base.onSite = value?.onSite ?? {}
    if (next === 'KEY_PICKUP') base.keyPickup = value?.keyPickup ?? {}
    if (next === 'THIRD_PARTY') base.thirdParty = value?.thirdParty ?? {}
    onChange(base)

    // Legacy sent the organization ProductKind, not the v2 mode name.
    const kind = MEETING_MODE_KIND[next]
    if (kind) {
      trackAnalytics({
        type: PfRevampEventName.MEETING_MODE_SELECTED,
        payload: { organizationMethod: kind },
      })
    }
  }

  // Which CARD is lit. On-site is two cards on one wire mode, so the mode alone
  // cannot answer it: the client's pick is remembered here and only falls back
  // to reading the draft's contact details when there is no pick yet (a resumed
  // order).
  const [pickedChoice, setPickedChoice] = useState<MeetingChoice | undefined>()
  const selectedChoice = pickedChoice ?? selectedMeetingChoice(
    mode ? { ...value, mode } as MeetingDTO : undefined,
    contactPrefill
  )

  /**
   * Choosing a card. Beyond `setMode` it decides whose details the on-site form
   * starts with — the question the "I will be onsite" checkbox used to ask after
   * the fact. Picking "someone else" CLEARS the fields rather than leaving the
   * client's own behind, which is the same thing unticking the checkbox did.
   */
  const setChoice = (next: MeetingChoice) => {
    setPickedChoice(next)
    const nextMode = choiceMode(next)
    setMode(nextMode)
    if (nextMode !== 'ON_SITE' && nextMode !== 'THIRD_PARTY') return

    const fills = choicePrefills(next)
    if (nextMode === 'ON_SITE') {
      const f = value?.onSite ?? {}
      onChange({
        mode: 'ON_SITE',
        contactIsSelf: fills,
        onSite: fills
          ? {
            ...f,
            contactName: f.contactName || contactPrefill?.name,
            contactPhone: f.contactPhone || contactPrefill?.phone,
            contactEmail: f.contactEmail || contactPrefill?.email,
          }
          : { ...f, contactName: undefined, contactPhone: undefined, contactEmail: undefined },
      })
    } else {
      // The third-party branch spells the same three fields without the
      // `contact` prefix — it is the same question about a different person.
      const f = value?.thirdParty ?? {}
      onChange({
        mode: 'THIRD_PARTY',
        contactIsSelf: fills,
        thirdParty: fills
          ? {
            ...f,
            name: f.name || contactPrefill?.name,
            phone: f.phone || contactPrefill?.phone,
            email: f.email || contactPrefill?.email,
          }
          : { ...f, name: undefined, phone: undefined, email: undefined },
      })
    }

    // Legacy only measured this for a client filling in their OWN details.
    const kind = MEETING_MODE_KIND[nextMode]
    if (prefillTarget === 'ME' && kind) {
      trackAnalytics({
        type: PfRevampEventName.MEETING_CONTACT_PREFILLED,
        payload: { organizationMethod: kind, filled: fills },
      })
    }
  }

  const toggleKeyPickupFillIn = () => {
    const next = !keyPickupFilledIn

    // Legacy measured only a client filling in their own details.
    const kind = MEETING_MODE_KIND.KEY_PICKUP
    if (kind && prefillTarget === 'ME') {
      trackAnalytics({
        type: PfRevampEventName.MEETING_CONTACT_PREFILLED,
        payload: { organizationMethod: kind, filled: next },
      })
    }

    // Seeds without overwriting anything typed; unticking clears the three fields.
    const f = value?.keyPickup ?? {}
    onChange({
      ...base(),
      contactIsSelf: next,
      keyPickup: next
        ? { ...f, contactName: f.contactName || contactPrefill?.name, contactPhone: f.contactPhone || contactPrefill?.phone, contactEmail: f.contactEmail || contactPrefill?.email }
        : { ...f, contactName: undefined, contactPhone: undefined, contactEmail: undefined },
    })
  }

  /** Whether the mode's form holds the client's own details: the self card is the one picked. */
  const isOwnCard = (m: MeetingMode): boolean => !!selectedChoice && choicePrefills(selectedChoice) && choiceMode(selectedChoice) === m

  /** Legacy's three-way email note: no reminder email for the client's own details, else the (trade) contact hint. */
  const emailHintKey = (m: MeetingMode): string => {
    const ownDetails = isOwnCard(m) || (m === 'KEY_PICKUP' && keyPickupFilledIn)
    if (ownDetails) return 'no_email_hint'
    return isMeasurementOnly ? 'contact_person_email_hint_measurement' : 'contact_person_email_hint'
  }

  const contactFields = (m: MeetingMode, contact: MeetingContact | undefined, onContact: (patch: MeetingContact) => void) => (
    <MeetingContactFields
      mode={m}
      contact={contact}
      onChange={onContact}
      ui={fieldUi(m)}
      // Only the mode on screen reports, so a hidden mode's stale verdict never blocks.
      onPhoneValidityChange={(invalid) => { if (m === mode) onPhoneValidityChange(invalid) }}
      countryCode={countryCode}
      emailHintKey={emailHintKey(m)}
      ownDetails={isOwnCard(m)}
    />
  )

  /** The selected card's form. On-site and key-pickup name the fields `contact*`; third party does not. */
  const fieldsForMode = (m: MeetingMode): React.ReactNode => {
    if (m === 'ON_SITE') {
      const f = value?.onSite
      return (
        <Grid container spacing={2}>
          {contactFields(m, { name: f?.contactName, phone: f?.contactPhone, email: f?.contactEmail }, (patch) =>
            onChange({ ...base(), onSite: { ...value?.onSite, ...toContactFields(patch) } }))}
        </Grid>
      )
    }

    if (m === 'KEY_PICKUP') {
      const f = value?.keyPickup
      const set = (patch: Partial<NonNullable<MeetingDTO['keyPickup']>>) =>
        onChange({ ...base(), keyPickup: { ...value?.keyPickup, ...patch } })
      return (
        <Grid container spacing={2}>

          <Grid item xs={12}>
            <Stack direction="row" gap={1} alignItems="center">
              <MUICheckbox
                id="pf-revamp-fill-in-meeting-info-KEY_PICKUP"
                checked={keyPickupFilledIn}
                onChange={toggleKeyPickupFillIn}
              />
              <label htmlFor="pf-revamp-fill-in-meeting-info-KEY_PICKUP" style={{ margin: 0, cursor: 'pointer' }}>
                {t(`order:step_instruction.meeting_info_checkbox.${MEETING_MODE_KIND.KEY_PICKUP}.${prefillTarget}`)}
              </label>
            </Stack>
          </Grid>

          {contactFields(m, { name: f?.contactName, phone: f?.contactPhone, email: f?.contactEmail }, (patch) => set(toContactFields(patch)))}

          <Grid item xs={12}>
            <InputWrap label={t('instruction_option_field:ADDRESS')} fieldNote={f?.address} required>
              <KeyPickupAddressField
                language={i18n.language}
                countryCode={countryCode}
                center={mapCenter}
                initialAddress={f?.address}
                initialMarker={f?.coordinates}
                onPlaceChange={(fields) => set({ address: fields.address, coordinates: fields.coordinates })}
              />
            </InputWrap>
          </Grid>

        </Grid>
      )
    }

    if (m === 'THIRD_PARTY') {
      return (
        <Grid container spacing={2}>
          {contactFields(m, value?.thirdParty, (patch) =>
            onChange({ ...base(), thirdParty: { ...value?.thirdParty, ...patch } }))}
        </Grid>
      )
    }

    return undefined
  }

  return (
    <SectionedBorderBox variant="flat"
      title={t('purchase_flow_revamp:instructions.meeting.section_title')}
      description={t('purchase_flow_revamp:instructions.meeting.section_description')}
    >
      <Stack gap={1.6}>


        {isModesLoading &&
          <PfInlineLoading message={t('purchase_flow_revamp:instructions.meeting.modes_loading')} />
        }

        {!isModesLoading &&
          <Stack gap={0.8}>

            <FormLabel>
              {t('purchase_flow_revamp:instructions.meeting.mode_label')}
            </FormLabel>

            <Stack gap={0.8} role="radiogroup" aria-label={t('purchase_flow_revamp:instructions.meeting.mode_label')}>
              {expandMeetingChoices(availableModes).map((choice) => {
                const m = choiceMode(choice)
                const kind = MEETING_MODE_KIND[m]
                const priceOption = kind ? organizationInstructions[kind] : undefined
                const hasPrice = (priceOption?.amount ?? 0) > 0
                const hint = t(`purchase_flow_revamp:instructions.meeting.${MEETING_CHOICE_KEY[choice]}_hint`, { defaultValue: '' })
                // Appended to the hint rather than gated on `hasPrice`: the travel fee
                // applies regardless of the organization fee's own amount.
                const travelCostNote = m === 'KEY_PICKUP'
                  ? t('purchase_flow_revamp:instructions.meeting.key_pickup_travel_cost_note')
                  : ''
                // On a measurement-only order a technician coordinates the
                // appointment, not a photographer, and legacy said so with its own
                // `_MEASUREMENT` description. The key carries a line break as a
                // `<0>` element, so it needs `Trans`, not `t`.
                const description = isMeasurementOnly && m === 'THIRD_PARTY'
                  ? (
                    <Trans t={t} i18nKey="product_kind_description:ORGANIZATION_THIRD_PARTY_MEASUREMENT">
                      <span style={{ display: 'block' }} />
                    </Trans>
                  )
                  : [hint, travelCostNote].filter(Boolean).join(' ') || undefined
                return (
                  <MUICheckCard
                    key={choice}
                    isRadio
                    checked={selectedChoice === choice}
                    onCheck={() => setChoice(choice)}
                    selectionStyle="ring"
                    title={t(`purchase_flow_revamp:instructions.meeting.${MEETING_CHOICE_KEY[choice]}`)}
                    description={description}
                    trailing={hasPrice &&
                      <Typography variant="text-sm" fontWeight={600} color={GRAY_900}>
                        {formatInstructionOptionPrice(priceOption as ResolvedInstructionOption, currency)}
                      </Typography>
                    }
                    expandableContent={fieldsForMode(m)}
                    data-cy={`pf-revamp-meeting-choice-${choice}`}
                  />
                )
              })}
            </Stack>

          </Stack>
        }

      </Stack>
    </SectionedBorderBox>
  )
}
